import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { PageHeader, fmtDate, fmtMoney, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle, XCircle, Copy, Download, QrCode, Trash, PaperPlaneTilt, EnvelopeSimple, ArrowClockwise } from "@phosphor-icons/react";

export default function PendingApplications() {
  const qc = useQueryClient();
  const qrRef = useRef(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteNote, setInviteNote] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["pending-applications"],
    queryFn: async () => (await api.get("/public/pending")).data,
  });

  const decide = useMutation({
    mutationFn: async ({ id, decision }) => (await api.post(`/public/pending/${id}/decide`, { decision })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["pending-applications"] });
      qc.invalidateQueries({ queryKey: ["interpreters"] });
      toast.success(vars.decision === "approved" ? "Interpreter approved — added to roster" : "Application rejected");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/public/pending/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pending-applications"] });
      toast.success("Application deleted");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  // --- Invitation flow ---
  const { data: invites = [] } = useQuery({
    queryKey: ["interpreter-invites"],
    queryFn: async () => (await api.get("/public/interpreter-invites")).data,
  });

  const invite = useMutation({
    mutationFn: async () => (await api.post("/public/interpreter-invites", {
      email: inviteEmail.trim(),
      name: inviteName.trim() || null,
      note: inviteNote.trim() || null,
      origin_url: window.location.origin,
    })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["interpreter-invites"] });
      setInviteEmail(""); setInviteName(""); setInviteNote("");
      if (d.email_disabled) {
        toast.warning("Invite saved — but email service isn't configured, so no email was sent");
      } else {
        toast.success(d.resend_count > 0 ? "Invitation re-sent" : "Invitation sent");
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  const resendInvite = useMutation({
    mutationFn: async (id) => (await api.post(`/public/interpreter-invites/${id}/resend`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["interpreter-invites"] });
      toast.success("Invitation re-sent");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Resend failed"),
  });

  const revokeInvite = useMutation({
    mutationFn: async (id) => (await api.delete(`/public/interpreter-invites/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["interpreter-invites"] });
      toast.success("Invitation revoked");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Revoke failed"),
  });

  const publicUrl = `${window.location.origin}/apply`;

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const serialized = new XMLSerializer().serializeToString(svg);
    // Rasterize to PNG at higher res
    const img = new Image();
    const svgBlob = new Blob([serialized], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const size = 1024;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = png;
      a.download = "unity-language-group-recruit-qr.png";
      a.click();
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  return (
    <div data-testid="pending-page">
      <PageHeader
        eyebrow="Recruitment"
        title="Pending Interpreter Applications"
        description="Review incoming interpreter applications from your public sign-up link."
      />

      {/* Recruit-link card with QR */}
      <div className="card-flat p-5 mb-6 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center" data-testid="recruit-card">
        <div ref={qrRef} className="p-3 bg-white rounded-md border border-slate-200 flex items-center justify-center">
          <QRCodeSVG value={publicUrl} size={160} level="M" includeMargin={false} bgColor="#FFFFFF" fgColor="#0B1120" data-testid="apply-qr" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#B91C1C] font-semibold">Grow the roster</div>
          <div className="font-display text-xl font-bold text-slate-900 mt-1">Your public interpreter application link</div>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            Share this QR code at conferences, on business cards, LinkedIn posts, or francophone community groups.
            Every scan opens the branded application form — new interpreters land here for your review.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <code className="font-mono text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1 truncate max-w-md">{publicUrl}</code>
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }} data-testid="copy-apply-link">
              <Copy size={12} className="mr-1" /> Copy link
            </Button>
            <Button size="sm" variant="outline" onClick={downloadQR} data-testid="download-qr">
              <Download size={12} className="mr-1" /> Download QR (PNG)
            </Button>
            <a href={publicUrl} target="_blank" rel="noreferrer">
              <Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                <QrCode size={12} className="mr-1" /> Preview form
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Invite by email */}
      <div className="card-flat p-5 mb-6" data-testid="invite-card">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#B91C1C] font-semibold">Direct outreach</div>
            <div className="font-display text-xl font-bold text-slate-900 mt-1">Invite an interpreter by email</div>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">Send someone a personal invitation with the apply link. They land on the same branded form and their submission shows up below for review.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Email <span className="text-red-500">*</span></label>
            <Input
              type="email"
              placeholder="candidate@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              data-testid="invite-email-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Name (optional)</label>
            <Input
              placeholder="Marie Dupont"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              data-testid="invite-name-input"
            />
          </div>
          <div className="md:pt-6">
            <Button
              onClick={() => invite.mutate()}
              disabled={invite.isPending || !inviteEmail.trim()}
              className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white w-full md:w-auto"
              data-testid="send-invite-btn"
            >
              <PaperPlaneTilt size={14} className="mr-2" /> {invite.isPending ? "Sending…" : "Send invitation"}
            </Button>
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Personal note (optional)</label>
          <Textarea
            rows={2}
            placeholder="e.g. Met you at the ATA conference — would love to have you on our medical roster."
            value={inviteNote}
            onChange={(e) => setInviteNote(e.target.value)}
            data-testid="invite-note-input"
          />
        </div>

        {invites.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-200" data-testid="invite-list">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Sent invitations · {invites.length}</div>
            <div className="space-y-2">
              {invites.map((iv) => (
                <div key={iv.invite_id} className="flex items-center justify-between p-2 rounded border border-slate-200 bg-slate-50/60" data-testid={`invite-row-${iv.invite_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <EnvelopeSimple size={14} className="text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-slate-800 truncate font-mono">{iv.email}</div>
                      <div className="text-[11px] text-slate-500">
                        {iv.name ? `${iv.name} · ` : ""}sent {fmtDate(iv.last_sent_at)}
                        {iv.resend_count > 0 && <> · resent {iv.resend_count}×</>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {iv.status === "applied" ? (
                      <span className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Applied</span>
                    ) : (
                      <span className="badge-pill bg-blue-50 text-blue-700 border-blue-200 text-[10px]">Sent</span>
                    )}
                    {iv.status !== "applied" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() => resendInvite.mutate(iv.invite_id)}
                        data-testid={`resend-invite-${iv.invite_id}`}
                      >
                        <ArrowClockwise size={12} className="mr-1" /> Resend
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs text-slate-500 hover:text-red-600"
                      onClick={() => {
                        if (window.confirm(`Revoke invitation for ${iv.email}?`)) revokeInvite.mutate(iv.invite_id);
                      }}
                      data-testid={`revoke-invite-${iv.invite_id}`}
                    >
                      <Trash size={12} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="card-flat p-10 text-center text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <Empty title="No pending applications" hint="Share the link above on LinkedIn or francophone community groups to grow your bench." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="pending-list">
          {rows.map((r) => (
            <div key={r.interpreter_id} className="card-flat p-5" data-testid={`pending-${r.interpreter_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-semibold text-slate-900">{r.full_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{r.email}  ·  {r.phone || "no phone"}</div>
                </div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-amber-700 font-semibold">Pending</div>
              </div>
              <div className="mt-3 text-xs text-slate-600 space-y-1">
                <div><b>Languages:</b> {r.language_pairs?.join(", ") || "—"}</div>
                <div><b>Specialties:</b> {r.specialties?.join(", ") || "—"}</div>
                <div><b>Location:</b> {r.location || "—"}</div>
                <div className="font-mono">
                  In-person {fmtMoney(r.hourly_rate)} · OPI {fmtMoney(r.opi_rate)} · VRI {fmtMoney(r.vri_rate)} · Trans ${(r.translation_rate || 0).toFixed(4)}/word
                </div>
                <div><b>W-9:</b> {r.w9_path ? <span className="text-emerald-700">on file ({r.w9_filename})</span> : <span className="text-amber-700">missing</span>}</div>
                <div><b>Applied:</b> {fmtDate(r.created_at)}</div>
                {r.notes && <div className="pt-1 text-slate-500 italic">"{r.notes}"</div>}
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => decide.mutate({ id: r.interpreter_id, decision: "approved" })} data-testid={`approve-${r.interpreter_id}`}>
                  <CheckCircle size={14} weight="fill" className="mr-1" /> Approve → add to roster
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => decide.mutate({ id: r.interpreter_id, decision: "rejected" })} data-testid={`reject-${r.interpreter_id}`}>
                  <XCircle size={14} weight="fill" className="mr-1" /> Reject
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-slate-500 hover:text-red-600 ml-auto"
                  data-testid={`delete-pending-${r.interpreter_id}`}
                  onClick={() => {
                    if (window.confirm(`Permanently delete application from ${r.full_name}? This removes it from the database.`)) {
                      del.mutate(r.interpreter_id);
                    }
                  }}
                >
                  <Trash size={14} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
